/**
 * PWWCapital — React Error Boundary (Section 30)
 *
 * Catches unhandled JS errors in any child component tree and renders
 * a user-friendly fallback instead of a broken blank screen.
 *
 * Usage:
 *   <ErrorBoundary fallback={<CustomFallback />}>
 *     <RiskyComponent />
 *   </ErrorBoundary>
 *
 *   // Or use the default fallback:
 *   <ErrorBoundary>
 *     <App />
 *   </ErrorBoundary>
 */

import React from 'react';
import ErrorFallback from './ErrorFallback';

class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
    this.handleReset = this.handleReset.bind(this);
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ errorInfo });

    // Log to console in development
    if (process.env.NODE_ENV === 'development') {
      console.error('[ErrorBoundary] Caught unhandled error:', error, errorInfo);
    }

    // Report to monitoring (Section 31) — non-blocking
    this.reportError(error, errorInfo);
  }

  reportError(error, errorInfo) {
    try {
      const payload = {
        message: error?.message ?? 'Unknown error',
        stack: process.env.NODE_ENV === 'development' ? error?.stack : undefined,
        componentStack: process.env.NODE_ENV === 'development'
          ? errorInfo?.componentStack
          : undefined,
        url: window.location.pathname,
        timestamp: new Date().toISOString(),
      };

      navigator.sendBeacon?.('/api/telemetry/errors/', JSON.stringify(payload));
    } catch {
      // Reporting failure must never cause a second error
    }
  }

  handleReset() {
    this.setState({ hasError: false, error: null, errorInfo: null });
  }

  render() {
    if (this.state.hasError) {
      // Allow callers to supply their own fallback
      if (this.props.fallback) {
        return React.cloneElement(this.props.fallback, {
          error: this.state.error,
          onReset: this.handleReset,
        });
      }

      return (
        <ErrorFallback
          error={this.state.error}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
